class SwiperCountupCopy extends HTMLElement {
	static DEBUG_KEY = 'swiper-countup-copy-debug';

	constructor() {
		super();
		this.defaultButtonText = '';
		this.defaultButtonUrl = '';
		this.slideConfig = [];
		this.boundUpdateButton = null;
	}

	connectedCallback() {
		this.defaultButtonText = this.dataset.defaultButtonText || '';
		this.defaultButtonUrl = this.dataset.defaultButtonUrl || '';
		this.slideConfig = this.getSlideConfig();

		this.log('connected', {
			defaultButtonText: this.defaultButtonText,
			defaultButtonUrl: this.defaultButtonUrl,
			slideConfig: this.slideConfig,
		});

		if (customElements.get('swiper-container')) {
			this.scheduleInit();
		} else {
			customElements.whenDefined('swiper-container').then(() => this.scheduleInit());
		}
	}

	disconnectedCallback() {
		this.unbindSwiperEvents();
	}

	isDebug() {
		return (
			this.dataset.debug === 'true' ||
			new URLSearchParams(window.location.search).has('swiper_countup_debug') ||
			window.localStorage.getItem(SwiperCountupCopy.DEBUG_KEY) === '1'
		);
	}

	log(...args) {
		if (!this.isDebug()) return;
		console.log('[swiper-countup-copy]', ...args);
	}

	warn(...args) {
		if (!this.isDebug()) return;
		console.warn('[swiper-countup-copy]', ...args);
	}

	getSlideConfig() {
		const configEl = this.querySelector('[data-slide-config]');

		if (!configEl) {
			this.warn('missing [data-slide-config] element');
			return [];
		}

		try {
			const parsed = JSON.parse(configEl.textContent || '[]');
			return Array.isArray(parsed) ? parsed : [];
		} catch (error) {
			this.warn('failed to parse slide config', error, configEl.textContent);
			return [];
		}
	}

	scheduleInit() {
		const swiperContainer = this.querySelector('swiper-container');
		if (!swiperContainer) {
			this.warn('swiper-container not found');
			return;
		}

		const boot = () => this.bindWhenReady(swiperContainer);

		if (boot()) return;

		swiperContainer.addEventListener('init', () => boot(), { once: true });
		setTimeout(() => boot(), 50);
		setTimeout(() => boot(), 250);
	}

	bindWhenReady(swiperContainer) {
		const swiper = swiperContainer.swiper;

		this.log('bindWhenReady', {
			hasSwiper: Boolean(swiper),
			initialized: swiper?.initialized,
			slideCount: swiper?.slides?.length,
			realIndex: swiper?.realIndex,
		});

		if (!swiper?.initialized) {
			return false;
		}

		this.bindSwiperEvents(swiper);
		this.updateButton(swiper);
		return true;
	}

	bindSwiperEvents(swiper) {
		if (this.boundUpdateButton) return;

		this.boundUpdateButton = () => {
			this.log('swiper event', {
				activeIndex: swiper.activeIndex,
				realIndex: swiper.realIndex,
			});
			this.updateButton(swiper);
		};

		swiper.on('slideChange', this.boundUpdateButton);
		swiper.on('realIndexChange', this.boundUpdateButton);

		this.log('bound swiper events');
	}

	unbindSwiperEvents() {
		const swiperContainer = this.querySelector('swiper-container');
		const swiper = swiperContainer?.swiper;

		if (swiper && this.boundUpdateButton) {
			swiper.off('slideChange', this.boundUpdateButton);
			swiper.off('realIndexChange', this.boundUpdateButton);
		}

		this.boundUpdateButton = null;
	}

	getActiveSlideData(swiper) {
		const index = typeof swiper.realIndex === 'number' ? swiper.realIndex : swiper.activeIndex;

		if (this.slideConfig[index]) {
			return this.slideConfig[index];
		}

		const swiperContainer = this.querySelector('swiper-container');
		const slide = swiperContainer?.querySelectorAll('swiper-slide')[index];

		return {
			buttonText: slide?.dataset?.buttonText || '',
			buttonUrl: slide?.dataset?.buttonUrl || '',
		};
	}

	updateButton(swiper) {
		const button = this.querySelector('[data-swiper-button]');
		if (!button) {
			this.warn('button [data-swiper-button] not found');
			return;
		}

		const slideData = this.getActiveSlideData(swiper);
		const buttonText = slideData.buttonText || this.defaultButtonText;
		const buttonUrl = slideData.buttonUrl || this.defaultButtonUrl;

		this.log('updateButton', {
			realIndex: swiper.realIndex,
			activeIndex: swiper.activeIndex,
			slideData,
			buttonText,
			buttonUrl,
		});

		button.textContent = buttonText;
		button.classList.toggle('hidden', !buttonText);

		if (buttonUrl) {
			button.setAttribute('href', buttonUrl);
		} else {
			button.removeAttribute('href');
		}
	}
}

customElements.define('swiper-countup-copy', SwiperCountupCopy);
